import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

interface GoogleCalendarStatus {
  connected: boolean
  email: string | null
  calendarId: string | null
  loading: boolean
  error: string | null
}

interface UseGoogleCalendarReturn {
  status: GoogleCalendarStatus
  connect: () => Promise<void>
  disconnect: () => Promise<boolean>
  refreshStatus: () => Promise<void>
}

export const useGoogleCalendar = (assistantId: string | null): UseGoogleCalendarReturn => {
  const { user } = useAuth()
  const [status, setStatus] = useState<GoogleCalendarStatus>({
    connected: false,
    email: null,
    calendarId: null,
    loading: true,
    error: null
  })

  const accountId = user?.account_id

  const fetchStatus = useCallback(async () => {
    if (!accountId || !assistantId || !supabase) {
      setStatus(prev => ({ ...prev, loading: false }))
      return
    }

    try {
      setStatus(prev => ({ ...prev, loading: true, error: null }))

      const { data, error } = await supabase
        .from('account_assistant_google_integrations')
        .select('google_email, google_calendar_id, is_active')
        .eq('assistant_id', assistantId)
        .eq('account_id', accountId)
        .eq('is_active', true)
        .maybeSingle()

      if (error) {
        console.error('[useGoogleCalendar] Error fetching status:', error)
        setStatus(prev => ({
          ...prev,
          loading: false,
          error: 'Erro ao verificar status da integracao'
        }))
        return
      }

      setStatus({
        connected: !!data,
        email: data?.google_email || null,
        calendarId: data?.google_calendar_id || null,
        loading: false,
        error: null
      })
    } catch (err) {
      console.error('[useGoogleCalendar] Unexpected error:', err)
      setStatus(prev => ({
        ...prev,
        loading: false,
        error: 'Erro inesperado'
      }))
    }
  }, [accountId, assistantId])

  const connect = useCallback(async () => {
    if (!accountId || !assistantId || !supabase) {
      setStatus(prev => ({ ...prev, error: 'Conta ou assistente nao identificado' }))
      return
    }

    try {
      setStatus(prev => ({ ...prev, loading: true, error: null }))

      const { data: { session } } = await supabase.auth.getSession()

      if (!session?.access_token) {
        setStatus(prev => ({ ...prev, loading: false, error: 'Sessao expirada' }))
        return
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const response = await fetch(`${supabaseUrl}/functions/v1/google-auth-init`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          account_id: accountId,
          assistant_id: assistantId,
          redirect_url: window.location.href,
          user_token: session.access_token
        })
      })

      const result = await response.json()

      if (!response.ok || !result.auth_url) {
        setStatus(prev => ({
          ...prev,
          loading: false,
          error: result.error || 'Erro ao iniciar autenticacao'
        }))
        return
      }

      const width = 600
      const height = 700
      const left = window.screenX + (window.outerWidth - width) / 2
      const top = window.screenY + (window.outerHeight - height) / 2

      const popup = window.open(
        result.auth_url,
        'google-oauth',
        `width=${width},height=${height},left=${left},top=${top},popup=yes`
      )

      if (!popup) {
        window.location.href = result.auth_url
        return
      }

      const handleMessage = (event: MessageEvent) => {
        if (event.data?.type === 'GOOGLE_AUTH_SUCCESS') {
          window.removeEventListener('message', handleMessage)
          setStatus({
            connected: true,
            email: event.data.email,
            calendarId: 'primary',
            loading: false,
            error: null
          })
        } else if (event.data?.type === 'GOOGLE_AUTH_ERROR') {
          window.removeEventListener('message', handleMessage)
          setStatus(prev => ({
            ...prev,
            loading: false,
            error: event.data.error || 'Erro na autenticacao'
          }))
        }
      }

      window.addEventListener('message', handleMessage)

      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed)
          window.removeEventListener('message', handleMessage)
          setTimeout(() => fetchStatus(), 1000)
        }
      }, 500)

    } catch (err) {
      console.error('[useGoogleCalendar] Connect error:', err)
      setStatus(prev => ({
        ...prev,
        loading: false,
        error: 'Erro ao conectar com Google'
      }))
    }
  }, [accountId, assistantId, fetchStatus])

  const disconnect = useCallback(async (): Promise<boolean> => {
    if (!accountId || !assistantId || !supabase) {
      setStatus(prev => ({ ...prev, error: 'Conta ou assistente nao identificado' }))
      return false
    }

    try {
      setStatus(prev => ({ ...prev, loading: true, error: null }))

      const { data: { session } } = await supabase.auth.getSession()

      if (!session?.access_token) {
        setStatus(prev => ({ ...prev, loading: false, error: 'Sessao expirada' }))
        return false
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const response = await fetch(`${supabaseUrl}/functions/v1/google-auth-disconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          account_id: accountId,
          assistant_id: assistantId,
          user_token: session.access_token
        })
      })

      const result = await response.json()

      if (!response.ok) {
        setStatus(prev => ({
          ...prev,
          loading: false,
          error: result.error || 'Erro ao desconectar'
        }))
        return false
      }

      setStatus({
        connected: false,
        email: null,
        calendarId: null,
        loading: false,
        error: null
      })

      return true
    } catch (err) {
      console.error('[useGoogleCalendar] Disconnect error:', err)
      setStatus(prev => ({
        ...prev,
        loading: false,
        error: 'Erro ao desconectar'
      }))
      return false
    }
  }, [accountId, assistantId])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])


  return {
    status,
    connect,
    disconnect,
    refreshStatus: fetchStatus
  }
}
